import { useEffect, useRef, useState } from 'react';

/** Tab-local recovery, scoped by actor/build/part. Never stores credentials or files. */
export function useEditorDraft<T>(key: string | undefined, initial: T): [T, React.Dispatch<React.SetStateAction<T>>, () => void] {
  const storageKey = key ? `bilt-editor-v1:${key}` : undefined;
  const [value, setValue] = useState<T>(() => {
    try {
      const saved = storageKey && sessionStorage.getItem(storageKey);
      if (saved) return { ...initial, ...JSON.parse(saved) };
    } catch { /* Storage can be unavailable in private browsing. */ }
    return initial;
  });
  useEffect(() => {
    if (!storageKey) return;
    try { sessionStorage.setItem(storageKey, JSON.stringify(value)); } catch { /* Keep the in-memory draft. */ }
  }, [storageKey, value]);
  const clear = () => { if (storageKey) { try { sessionStorage.removeItem(storageKey); } catch { /* Best effort. */ } } };
  return [value, setValue, clear];
}

/** Browser/native Back closes the editor through its existing discard/save guard. */
export function useEditorBack(active: boolean, requestClose: () => void, hasWork: boolean) {
  const closeRef = useRef(requestClose);
  closeRef.current = requestClose;
  useEffect(() => {
    if (!active) return;
    const marker = `editor-${Date.now()}-${Math.random()}`;
    const url = window.location.href;
    const previousState = window.history.state;
    let installed = false;
    const timer = window.setTimeout(() => {
      installed = true;
      window.history.pushState({ ...previousState, biltEditor: marker }, '', url);
    }, 0);
    const onBack = () => {
      if (!installed || window.history.state?.biltEditor === marker) return;
      // Stay at the selected build until the user explicitly closes/discards.
      window.history.pushState({ ...previousState, biltEditor: marker }, '', url);
      closeRef.current();
    };
    window.addEventListener('popstate', onBack);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('popstate', onBack);
      if (window.history.state?.biltEditor === marker) window.history.back();
    };
  }, [active]);
  useEffect(() => {
    if (!active || !hasWork) return;
    const onUnload = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [active, hasWork]);
}
